/** Normalized visa payload (Edge Function + cache). */
export type VisaSummary = {
  visa_status: string
  max_stay?: number
  currency?: string
  e_visa_available?: boolean
  passport_validity_required_months?: number
  notes?: string
  required_documents?: string[]
  available_visa_types?: string[]
  _source?: string
  _stale?: boolean
  _meta?: { passport?: string; destination?: string; normalized_at?: string }
}

export type VisaCheckFnResponse = {
  ok: boolean
  data?: VisaSummary | null
  error?: string
  source?: "cache" | "live" | "cache_stale" | "dev_fallback" | "visa_metadata"
  warning?: string
  last_checked_at?: string
  /** Present when response is based on `visa_metadata`. */
  verification_status?: string | null
}

export const DOCUMENT_SOURCE_NOTE =
  "Source: Wundrlst visa metadata (human-verified routes when marked verified). Always confirm with the official authority."

export function isVisaFreeStatus(visaStatus: string | undefined | null): boolean {
  if (!visaStatus) return false
  const s = visaStatus.toLowerCase()
  return (
    s === "visa_free" ||
    s.includes("visa_free") ||
    s.includes("visa free") ||
    s.includes("visa not required") ||
    s.includes("no visa required") ||
    s.includes("no visa") ||
    s.includes("no_visa") ||
    s === "waiver" ||
    s.includes("waiver")
  )
}

export function isVisaOnArrival(visaStatus: string | undefined | null): boolean {
  if (!visaStatus) return false
  return visaStatus.toLowerCase().includes("on_arrival") || visaStatus.toLowerCase().includes("on arrival")
}

/**
 * Values written by onboarding / workspace wizards into `ProvisionWorkspaceInput.visaStatus`.
 * These describe **user intent** (applying vs already holds visa), not a passport entry outcome
 * from `visa_metadata` / API — they must not drive `isVisaFreeStatus`-style shortcuts.
 */
export function isWizardUserVisaIntentStatus(visaStatus: string | null | undefined): boolean {
  if (!visaStatus?.trim()) return false
  const s = visaStatus.trim().toLowerCase()
  return s === "applying_new" || s === "valid_existing" || s === "unknown"
}

/**
 * True only for **visa-free** passport entry (not VOA / eVisa / embassy visa).
 * VOA still requires obtaining a visa at the border — do not treat it as visa-free.
 */
export function isPassportVisaFreeEntry(visaStatus: string | null | undefined): boolean {
  if (isWizardUserVisaIntentStatus(visaStatus)) return false
  if (isVisaOnArrival(visaStatus)) return false
  return isVisaFreeStatus(visaStatus)
}

/**
 * @deprecated Prefer {@link isPassportVisaFreeEntry} or {@link isVisaOnArrival}.
 * Historically treated VOA as an exemption; that conflation is incorrect for product copy.
 * Kept as an alias of visa-free-only so existing call sites stop labeling VOA as "no visa".
 */
export function passportEntryExemptForWorkspaceProvisioning(
  visaStatus: string | null | undefined
): boolean {
  return isPassportVisaFreeEntry(visaStatus)
}
